import { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  RefreshCw, 
  Trash2, 
  ImagePlus,
  Link2,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function ImageUploader({ value, onChange, onRemove, placeholder = "Add an image" }) {
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileSelect = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${API}/api/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onChange(data.url);
        toast.success('Image uploaded successfully');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to upload image');
      }
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    handleFileSelect(file);
  };

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
      setUrlInput('');
      setShowUrlInput(false);
    }
  };

  if (value) {
    return (
      <div className="relative group rounded-lg overflow-hidden bg-black/5">
        <img src={value} alt="Preview" className="w-full h-auto object-cover" />
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/90 hover:bg-white text-gray-900"
            onClick={() => fileInputRef.current?.click()}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Replace
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={onRemove}
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Remove
          </Button>
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className={`relative border-2 border-dashed rounded-xl p-8 transition-all duration-200 cursor-pointer ${
          dragOver 
            ? 'border-[#B89B5E] bg-[#B89B5E]/10' 
            : 'border-gray-300 hover:border-[#B89B5E]/60 hover:bg-gray-50'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFileSelect(e.target.files[0])}
        />
        <div className="flex flex-col items-center justify-center gap-3 text-center">
          {uploading ? (
            <div className="w-10 h-10 border-2 border-[#B89B5E] border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <div className="w-14 h-14 rounded-full bg-[#B89B5E]/10 flex items-center justify-center">
                <ImagePlus className="w-7 h-7 text-[#B89B5E]" />
              </div>
              <div>
                <p className="font-medium text-gray-700">{placeholder}</p>
                <p className="text-sm text-gray-500 mt-1">Drag & drop or click to upload</p>
              </div>
            </>
          )}
        </div>
      </div>
      
      {/* URL Input Option */}
      {showUrlInput ? (
        <div className="flex gap-2">
          <Input
            placeholder="Paste image URL..."
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
            className="flex-1"
          />
          <Button size="sm" onClick={handleUrlSubmit} className="bg-[#B89B5E] hover:bg-[#A08050]">
            <Check className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setShowUrlInput(false)}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <button
          onClick={() => setShowUrlInput(true)}
          className="w-full py-2 text-sm text-gray-500 hover:text-[#B89B5E] transition-colors flex items-center justify-center gap-2"
        >
          <Link2 className="w-4 h-4" />
          Or paste image URL
        </button>
      )}
    </div>
  );
}
